import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agentInstanceService } from '../../../services/agentService';
import { Button } from '../../ui/Button';
import { Checkbox, SelectField, TextField } from '../../ui/Field';
import { Surface } from '../../ui/Surface';

interface Props {
  agent: any;
}

type FieldType = 'text' | 'email' | 'phone' | 'number' | 'date' | 'datetime' | 'url' | 'enum';

interface LeadField {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  optionsText?: string; // comma-separated, only for enum
}

interface LeadService {
  id: string;
  name: string;
  description: string;
  fields: LeadField[];
  /** When true, the conversation/call ends (with closingMessage) once this service's fields are captured. */
  endOnComplete: boolean;
  /** Closing line used when endOnComplete is on; falls back to a generic default if left blank. */
  closingMessage: string;
}

const FIELD_TYPES: FieldType[] = ['text', 'email', 'phone', 'number', 'date', 'datetime', 'url', 'enum'];

const emptyField = (): LeadField => ({ key: '', label: '', type: 'text', required: true });

function toEditableField(f: any): LeadField {
  return {
    key: f?.key ?? '',
    label: f?.label ?? '',
    type: FIELD_TYPES.includes(f?.type) ? f.type : 'text',
    required: f?.required !== false,
    optionsText: Array.isArray(f?.options) ? f.options.map((o: any) => o.value ?? o.label ?? '').join(', ') : '',
  };
}

function serializeField(f: LeadField): any {
  const out: any = { key: f.key.trim(), label: f.label.trim(), type: f.type, required: f.required };
  if (f.type === 'enum' && f.optionsText?.trim()) {
    out.options = f.optionsText.split(',').map((s) => s.trim()).filter(Boolean).map((v) => ({ label: v, value: v }));
  }
  return out;
}

const FieldRows: React.FC<{ fields: LeadField[]; onChange: (fields: LeadField[]) => void }> = ({ fields, onChange }) => {
  const update = (i: number, patch: Partial<LeadField>) => {
    const next = fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f));
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-start">
          <TextField tone="strong" className="col-span-3 px-2 py-1.5 border rounded text-sm" placeholder="key (e.g. fullName)"
            value={f.key} onChange={(e) => update(i, { key: e.target.value })} />
          <TextField tone="strong" className="col-span-3 px-2 py-1.5 border rounded text-sm" placeholder="label (e.g. your full name)"
            value={f.label} onChange={(e) => update(i, { label: e.target.value })} />
          <SelectField tone="strong" className="col-span-2 px-2 py-1.5 border rounded text-sm"
            value={f.type} onChange={(e) => update(i, { type: e.target.value as FieldType })}>
            {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </SelectField>
          {f.type === 'enum' ? (
            <TextField tone="strong" className="col-span-3 px-2 py-1.5 border rounded text-sm" placeholder="options: a, b, c"
              value={f.optionsText ?? ''} onChange={(e) => update(i, { optionsText: e.target.value })} />
          ) : <div className="col-span-3" />}
          <label className="col-span-1 flex items-center gap-1 text-xs text-gray-600 pt-1.5">
            <Checkbox checked={f.required} onChange={(e) => update(i, { required: e.target.checked })} /> req
          </label>
          <Button tone="danger-link" type="button" className="col-span-12 text-left text-xs"
            onClick={() => onChange(fields.filter((_, idx) => idx !== i))}>Remove field</Button>
        </div>
      ))}
      <Button tone="custom" type="button" className="text-sm text-blue-600 hover:text-blue-800"
        onClick={() => onChange([...fields, emptyField()])}>+ Add field</Button>
    </div>
  );
};

const EmailRows: React.FC<{ emails: string[]; onChange: (emails: string[]) => void }> = ({ emails, onChange }) => {
  const update = (i: number, value: string) => onChange(emails.map((e, idx) => (idx === i ? value : e)));
  return (
    <div className="space-y-2">
      {emails.map((email, i) => (
        <div key={i} className="flex items-center gap-2">
          <TextField tone="strong" className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="name@example.com"
            value={email} onChange={(e) => update(i, e.target.value)} type="email" />
          <Button tone="danger-link" type="button" className="text-xs whitespace-nowrap"
            onClick={() => onChange(emails.filter((_, idx) => idx !== i))}>Remove</Button>
        </div>
      ))}
      <Button tone="custom" type="button" className="text-sm text-blue-600 hover:text-blue-800"
        onClick={() => onChange([...emails, ''])}>+ Add email</Button>
    </div>
  );
};

const LeadCaptureConfigTab: React.FC<Props> = ({ agent }) => {
  const [commonFields, setCommonFields] = useState<LeadField[]>([]);
  const [services, setServices] = useState<LeadService[]>([]);
  const [submitAction, setSubmitAction] = useState('lead-capture-submit');
  const [introMessage, setIntroMessage] = useState('');
  const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
  const [captureMode, setCaptureMode] = useState<'sequential' | 'form'>('sequential');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!agent) return;
    const ai = agent.aiSettings ?? {};
    const cfg = ai.leadCaptureConfig ?? ai.settings?.leadCaptureConfig ?? {};
    setCommonFields((cfg.commonFields ?? []).map(toEditableField));
    setServices((cfg.services ?? []).map((s: any) => ({
      id: s.id ?? '', name: s.name ?? '', description: s.description ?? '',
      fields: (s.fields ?? []).map(toEditableField),
      endOnComplete: !!s.endOnComplete,
      closingMessage: s.closingMessage ?? '',
    })));
    setSubmitAction(cfg.submitActionCapabilityCode ?? 'lead-capture-submit');
    setIntroMessage(cfg.introMessage ?? '');
    setNotifyEmails(Array.isArray(cfg.notifyEmails) ? cfg.notifyEmails : []);
    setCaptureMode(cfg.captureMode === 'form' ? 'form' : 'sequential');
  }, [agent]);

  const addService = () =>
    setServices((prev) => [...prev, { id: '', name: '', description: '', fields: [], endOnComplete: false, closingMessage: '' }]);
  const updateService = (i: number, patch: Partial<LeadService>) =>
    setServices((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const handleSave = async () => {
    if (!agent?.id) { toast.error('Agent id missing.'); return; }
    // Services are optional — an agent with a single offering can capture leads
    // using only the common fields below, with no service selection step.
    for (const s of services) if (!s.id.trim() || !s.name.trim()) { toast.error('Each service needs an id and name.'); return; }

    const leadCaptureConfig = {
      commonFields: commonFields.map(serializeField),
      services: services.map((s) => ({
        id: s.id.trim(), name: s.name.trim(), description: s.description.trim(),
        fields: s.fields.map(serializeField),
        endOnComplete: s.endOnComplete,
        closingMessage: s.closingMessage.trim() || undefined,
      })),
      submitActionCapabilityCode: submitAction.trim() || 'lead-capture-submit',
      introMessage: introMessage.trim() || undefined,
      notifyEmails: notifyEmails.map((e) => e.trim()).filter(Boolean),
      captureMode,
    };

    setSaving(true);
    try {
      // Send only the slice this tab owns. `leadCaptureConfig` lives inside
      // `settings` alongside the AI Settings / Instructions tabs' keys; the
      // ai-settings endpoint shallow-merges `settings` server-side, so echoing
      // model/temperature/instructionGroups/llm here (from a possibly-stale copy)
      // is exactly what would clobber those other tabs.
      const payload = {
        schemaVersion: Number(agent?.version) || 1,
        settings: {
          leadCaptureConfig,
        },
      };
      await agentInstanceService.updateAgentInstanceAiSettings(encodeURIComponent(agent.id), payload);
      toast.success('Lead capture configuration saved.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save lead capture config.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Lead Capture</h2>
        <p className="text-sm text-gray-500 mt-1">
          Define the fields to collect. Common fields are captured for every service; service-specific fields are
          collected only when the visitor selects that service. The agent also answers questions from the knowledge base.
        </p>
      </div>

      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Lead Capture Mode</h3>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="radio" name="capture-mode" checked={captureMode === 'sequential'}
              onChange={() => setCaptureMode('sequential')} />
            One by One Questions
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="radio" name="capture-mode" checked={captureMode === 'form'}
              onChange={() => setCaptureMode('form')} />
            Embedded Lead Capture Form
          </label>
        </div>
        <p className="text-xs text-gray-400">
          One by One Questions asks for each field conversationally, one at a time. Embedded Lead
          Capture Form shows a single form with every field at once instead (chat only — voice calls
          always ask one at a time). Changing this only affects NEW conversations.
        </p>
      </Surface>

      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Common Fields (always captured)</h3>
        <FieldRows fields={commonFields} onChange={setCommonFields} />
      </Surface>

      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Services (optional)</h3>
            <p className="text-xs text-gray-400 mt-1">
              Only needed if this agent handles multiple offerings the visitor should choose between. Leave empty for a
              single-service agent — only the common fields above will be captured.
            </p>
          </div>
          <Button tone="custom" type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={addService}>+ Add service</Button>
        </div>
        {services.map((s, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-12 gap-2">
              <TextField tone="strong" className="col-span-3 px-2 py-1.5 border rounded text-sm" placeholder="service id (e.g. coaching)"
                value={s.id} onChange={(e) => updateService(i, { id: e.target.value })} />
              <TextField tone="strong" className="col-span-4 px-2 py-1.5 border rounded text-sm" placeholder="name (e.g. Career Coaching)"
                value={s.name} onChange={(e) => updateService(i, { name: e.target.value })} />
              <TextField tone="strong" className="col-span-5 px-2 py-1.5 border rounded text-sm" placeholder="description (optional)"
                value={s.description} onChange={(e) => updateService(i, { description: e.target.value })} />
            </div>
            <div className="pl-2">
              <p className="text-xs font-medium text-gray-600 mb-1">Fields for this service</p>
              <FieldRows fields={s.fields} onChange={(fields) => updateService(i, { fields })} />
            </div>
            <div className="pl-2 space-y-2 border-t border-gray-100 pt-3">
              <p className="text-xs font-medium text-gray-600">After capturing this service's info</p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-xs text-gray-700">
                  <input type="radio" name={`end-behavior-${i}`} checked={!s.endOnComplete}
                    onChange={() => updateService(i, { endOnComplete: false })} />
                  Continue the conversation
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-700">
                  <input type="radio" name={`end-behavior-${i}`} checked={s.endOnComplete}
                    onChange={() => updateService(i, { endOnComplete: true })} />
                  End the conversation
                </label>
              </div>
              {s.endOnComplete && (
                <TextField tone="strong" className="w-full px-2 py-1.5 border rounded text-sm"
                  placeholder="Closing message (optional — e.g. Thanks! We'll be in touch shortly.)"
                  value={s.closingMessage} onChange={(e) => updateService(i, { closingMessage: e.target.value })} />
              )}
            </div>
            <Button tone="danger-link" type="button" className="text-xs"
              onClick={() => setServices((prev) => prev.filter((_, idx) => idx !== i))}>Remove service</Button>
          </div>
        ))}
        {services.length === 0 && <p className="text-sm text-gray-400">No services yet — add one to define its fields.</p>}
      </Surface>

      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Submission</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Submit Action Capability Code</label>
          <TextField tone="strong" className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
            value={submitAction} onChange={(e) => setSubmitAction(e.target.value)} placeholder="lead-capture-submit" />
          <p className="text-xs text-gray-400 mt-1">
            The capability code of the Action (HTTP or MCP, configured in the Actions tab) that persists the captured lead.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Intro Message (optional)</label>
          <TextField tone="strong" className="w-full px-3 py-2 border rounded-lg text-sm"
            value={introMessage} onChange={(e) => setIntroMessage(e.target.value)} placeholder="Hi! I can answer questions and help get you started." />
        </div>
      </Surface>

      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Email Notifications</h3>
          <p className="text-xs text-gray-400 mt-1">
            Send a copy of each captured lead to these email addresses, in addition to the Submit Action above.
          </p>
        </div>
        <EmailRows emails={notifyEmails} onChange={setNotifyEmails} />
      </Surface>

      <div className="flex justify-end">
        <Button tone="primary" type="button" disabled={saving} onClick={handleSave}
          className="px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60 hover:opacity-90">
          {saving ? 'Saving…' : 'Save Lead Capture Config'}
        </Button>
      </div>
    </div>
  );
};

export default LeadCaptureConfigTab;
